import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const Eyebrow = ({ children, light = false }) => (
  <p className={`font-mono-tech text-xs font-semibold uppercase tracking-[0.25em] flex items-center gap-2.5 ${light ? "text-red-400" : "text-[#C5221F]"}`} data-testid="eyebrow">
    <span className={`inline-block w-6 h-px ${light ? "bg-red-400" : "bg-[#C5221F]"}`} aria-hidden="true" />
    {children}
  </p>
);

export const SectionHead = ({ eyebrow, title, sub, light = false }) => (
  <div className="max-w-3xl">
    <Reveal><Eyebrow light={light}>{eyebrow}</Eyebrow></Reveal>
    <Reveal delay={0.08}>
      <h2 className={`font-display mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight ${light ? "text-white" : "text-slate-900"}`}>
        {title}
      </h2>
    </Reveal>
    {sub && (
      <Reveal delay={0.16}>
        <p className={`mt-5 text-base leading-relaxed ${light ? "text-slate-400" : "text-slate-600"}`}>{sub}</p>
      </Reveal>
    )}
  </div>
);

export const Counter = ({ value, suffix = "", plain = false }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView || plain) return;
    const dur = 1400;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min((t - t0) / dur, 1);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value, plain]);
  return <span ref={ref}>{plain ? value : n.toLocaleString("en-IN")}{suffix}</span>;
};

export const PageHero = ({ img, eyebrow, title, sub }) => (
  <section className="relative bg-[#0A1128] overflow-hidden" data-testid="page-hero">
    <div className="absolute inset-0">
      <img src={img} alt="" className="w-full h-full object-cover img-industrial opacity-40 animate-kenburns" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A1128] via-[#0A1128]/80 to-[#0A1128]/30" />
      <div className="absolute inset-0 blueprint-grid opacity-70" />
      <div className="absolute left-0 top-0 bottom-0 w-px bg-white/10 hidden lg:block" />
    </div>
    <div className="container-gre relative py-24 lg:py-32">
      <Reveal><Eyebrow light>{eyebrow}</Eyebrow></Reveal>
      <Reveal delay={0.08}>
        <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05] max-w-3xl">{title}</h1>
      </Reveal>
      {sub && <Reveal delay={0.16}><p className="mt-6 text-base lg:text-lg text-slate-300 max-w-2xl leading-relaxed">{sub}</p></Reveal>}
    </div>
    <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#C5221F]" />
  </section>
);

export const CTASection = () => (
  <section className="relative bg-[#0A1128] overflow-hidden" data-testid="cta-section">
    <div className="absolute inset-0 blueprint-grid" />
    <div className="absolute -left-24 top-0 w-96 h-96 bg-[#C5221F]/10 rotate-45" />
    <div className="container-gre relative py-20 lg:py-28">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
        <div className="max-w-2xl">
          <Reveal><Eyebrow light>Start a conversation</Eyebrow></Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Let's Build What's Next
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-5 text-slate-400 text-base lg:text-lg leading-relaxed">
              Discuss your fabrication requirements, EPC projects, heavy equipment, site fabrication or project execution with our engineering team.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <Link
            to="/contact"
            data-testid="cta-contact-team-btn"
            className="group inline-flex items-center gap-3 bg-[#C5221F] hover:bg-[#A81B18] text-white font-display font-semibold px-8 py-4 text-sm uppercase tracking-widest transition-colors duration-300"
          >
            Contact Our Team
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

export const SpecTable = ({ rows, light = false }) => (
  <dl className={`border ${light ? "border-slate-700" : "border-slate-200"}`} data-testid="spec-table">
    {rows.map(([k, v], i) => (
      <div key={k} className={`grid grid-cols-2 gap-4 px-5 py-3.5 ${i % 2 === 0 ? (light ? "bg-white/5" : "bg-slate-50") : ""} ${i > 0 ? (light ? "border-t border-slate-700" : "border-t border-slate-200") : ""}`}>
        <dt className="font-mono-tech text-[11px] font-semibold uppercase tracking-widest text-slate-500 self-center">{k}</dt>
        <dd className={`text-sm font-semibold text-right ${light ? "text-white" : "text-slate-900"}`}>{v}</dd>
      </div>
    ))}
  </dl>
);

let metaOverridesPromise = null;
const getMetaOverrides = () => {
  if (!metaOverridesPromise) {
    metaOverridesPromise = fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pages-meta`)
      .then((r) => (r.ok ? r.json() : {}))
      .catch(() => ({}));
  }
  return metaOverridesPromise;
};

export const usePageMeta = (title, description) => {
  useEffect(() => {
    let active = true;
    const path = window.location.pathname;
    getMetaOverrides().then((ov) => {
      if (!active) return;
      const o = (ov && ov[path]) || {};
      document.title = o.seo_title || title;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", o.seo_description || description || "");
    });
    return () => { active = false; };
  }, [title, description]);
};
